(() => {
  const QUANTITY = 'input[data-cart-quantity]';
  const ACTIONS = '[data-cart-remove], [data-cart-quantity-action], [data-cart-checkout], [data-cart-update]';
  const integer = value => typeof value === 'number' && Number.isSafeInteger(value);
  const wholeNumber = value => typeof value === 'string' && /^\d+$/.test(value) && Number.isSafeInteger(Number(value));

  function quantityIsValid(input, value) {
    const minimum = Number(input.dataset.min);
    const step = Number(input.step);
    const maximum = input.max === '' ? Infinity : Number(input.max);
    return integer(value) && value >= 0 && (value === 0 ||
      integer(minimum) && minimum > 0 && integer(step) && step > 0 && value >= minimum && value <= maximum && value % step === 0);
  }

  function unique(root, selector) {
    const nodes = root.querySelectorAll(selector);
    if (nodes.length !== 1) throw new Error('Invalid cart section.');
    return nodes[0];
  }

  function cartParts(host) {
    const form = unique(host, 'form[data-cart-form]');
    const content = unique(host, '[data-cart-content]');
    if (!form.contains(content) || content.querySelector('[data-cart-note]')) throw new Error('Invalid cart form.');
    const count = unique(content, '[data-cart-count]');
    const items = unique(content, '[data-cart-items]');
    const summary = unique(content, '[data-cart-summary]');
    const empty = unique(content, '[data-cart-empty]');
    const regions = [items, summary, empty];
    if (new Set(regions).size !== 3 || regions.some(outer => regions.some(inner => outer !== inner && outer.contains(inner)))) throw new Error('Invalid cart regions.');
    const rows = [...content.querySelectorAll('[data-cart-item]')];
    if (rows.length !== items.children.length || rows.some(row => row.parentElement !== items)) throw new Error('Invalid cart rows.');
    return { form, content, count, items, summary, empty, rows };
  }

  function validatedSection(data, sectionId) {
    if (!data || !integer(data.item_count) || data.item_count < 0 || !Array.isArray(data.items)) throw new Error('Invalid cart response.');
    const quantities = new Map();
    let total = 0;
    for (const item of data.items) {
      if (!item || typeof item.key !== 'string' || !item.key.trim() || quantities.has(item.key) || !integer(item.quantity) || item.quantity <= 0) throw new Error('Invalid cart item.');
      quantities.set(item.key, item.quantity);
      total += item.quantity;
    }
    if (total !== data.item_count) throw new Error('Invalid cart count.');
    const html = data.sections?.[sectionId];
    if (typeof html !== 'string' || !html.trim()) throw new Error('Missing cart section.');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const host = unique(parsed, 'ai-cart-items');
    if (host.dataset.cartSectionId !== sectionId || parsed.querySelectorAll('form').length !== 1 || parsed.querySelectorAll('[data-cart-content]').length !== 1) throw new Error('Invalid cart section identity.');
    const parts = cartParts(host);
    if (parts.rows.length !== quantities.size || Number(parts.count.textContent.trim()) !== total) throw new Error('Invalid rendered cart count.');
    const keys = new Set();
    for (const row of parts.rows) {
      const key = row.dataset.cartKey;
      const input = unique(row, QUANTITY);
      const value = input.getAttribute('value');
      if (!key || keys.has(key) || input.type !== 'number' || input.name !== 'updates[]' || input.min !== '0' || !wholeNumber(value) || Number(value) <= 0 || quantities.get(key) !== Number(value)) throw new Error('Invalid rendered cart quantity.');
      keys.add(key);
    }
    return parts;
  }

  function responseMessage(data) {
    if (typeof data?.description === 'string' && data.description.trim()) return data.description;
    if (typeof data?.errors === 'string' && data.errors.trim()) return data.errors;
    if (data?.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors).flat().filter(value => typeof value === 'string' && value.trim());
      if (messages.length) return messages.join(' ');
    }
    return typeof data?.message === 'string' ? data.message : '';
  }

  class AiCartItems extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.version = (this.version || 0) + 1;
      this.parts = cartParts(this);
      this.sectionId = this.dataset.cartSectionId;
      this.note = this.parts.form.querySelector('textarea[data-cart-note]');
      this.error = this.querySelector('[data-cart-error]');
      this.errorMessage = this.querySelector('[data-cart-error-message]');
      this.defaultError ||= this.errorMessage?.textContent || 'Please refresh the cart to check its current state.';
      const interrupted = this.busy;
      if (interrupted) this.setBusy(false);
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('change', event => this.onChange(event), { signal });
      this.addEventListener('input', event => this.onInput(event), { signal });
      this.addEventListener('click', event => this.onClick(event), { signal, capture: true });
      this.addEventListener('submit', event => {
        if (event.target !== this.parts.form) return;
        if (this.busy) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        clearTimeout(this.noteTimer);
      }, { signal, capture: true });
      this.setAttribute('aria-busy', 'false');
      this.syncNoteVisibility(Number(this.parts.count.textContent.trim()));
      const refresh = this.querySelector('a[data-cart-refresh]');
      if (refresh) {
        const url = new URL(this.dataset.cartUrl, location.href);
        for (const [name, value] of new URLSearchParams(location.search)) {
          if (!['sections', 'section_id'].includes(name) && !url.searchParams.has(name)) url.searchParams.append(name, value);
        }
        refresh.href = url.pathname + url.search;
      }
      if (interrupted) {
        this.resetQuantities();
        this.showError();
      }
    }

    disconnectedCallback() {
      this.version++;
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.noteTimer);
      // A disconnected view cannot infer whether its request changed the server cart.
    }

    onInput(event) {
      if (this.busy) return;
      if (event.target.matches(QUANTITY)) {
        event.target.setCustomValidity('');
        const error = event.target.closest('[data-cart-item]')?.querySelector('[data-cart-item-error]');
        if (error) { error.textContent = ''; error.hidden = true; }
      }
      if (event.target === this.note) {
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.saveNote(), 300);
      }
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches(QUANTITY) || input.form !== this.parts.form) return;
      if (this.busy || input.disabled || input.readOnly) { this.resetQuantity(input); return; }
      if (input.value === '' && !input.validity.badInput) return;
      input.setCustomValidity('');
      const value = Number(input.value);
      if (!wholeNumber(input.value) || !quantityIsValid(input, value)) {
        const chinese = document.documentElement.lang.startsWith('zh');
        const message = chinese
          ? `请输入有效整数；正数量至少为 ${input.dataset.min}，按 ${input.step} 递增${input.max ? `，最多 ${input.max}` : ''}。`
          : `Enter a whole number; positive quantities must be at least ${input.dataset.min}, in multiples of ${input.step}${input.max ? `, up to ${input.max}` : ''}.`;
        const error = input.closest('[data-cart-item]')?.querySelector('[data-cart-item-error]');
        if (error) { error.textContent = message; error.hidden = false; }
        input.setCustomValidity(message);
        input.reportValidity();
        this.resetQuantity(input);
        return;
      }
      if (value === Number(input.getAttribute('value'))) return;
      const row = input.closest('[data-cart-item]');
      if (row?.dataset.cartKey) this.changeQuantity(row, value, input);
    }

    onClick(event) {
      const action = event.target.closest(ACTIONS);
      if (!action || !this.parts.form.contains(action)) return;
      if (this.busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!action.matches('a[data-cart-remove]') || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!action.hasAttribute('href') || action.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
      const row = action.closest('[data-cart-item]');
      if (!row?.dataset.cartKey) return;
      event.preventDefault();
      this.changeQuantity(row, 0, action);
    }

    endpoint(route) {
      const url = new URL(route, location.href);
      if (url.origin !== location.origin) throw new Error('Invalid cart route.');
      if (!url.pathname.endsWith('.js')) url.pathname += '.js';
      return url.pathname + url.search;
    }

    async post(route, body) {
      const url = this.endpoint(route);
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok || data?.errors || Number(data?.status) >= 400) {
        const error = new Error('Cart request failed.');
        error.cartMessage = responseMessage(data);
        throw error;
      }
      return data;
    }

    current(version) { return this.isConnected && this.controller && version === this.version; }

    async changeQuantity(row, quantity, source) {
      if (this.busy) return;
      const version = this.version;
      const focused = row.contains(document.activeElement) ? document.activeElement : source;
      const focus = { key: row.dataset.cartKey, index: this.parts.rows.indexOf(row), action: focused.dataset.cartQuantityAction, remove: focused.hasAttribute('data-cart-remove') };
      this.clearErrors();
      this.setBusy(true);
      this.showStatus('updating');
      try {
        const data = await this.post(this.dataset.cartChangeUrl, { id: row.dataset.cartKey, quantity, sections: [this.sectionId], sections_url: location.pathname + location.search });
        if (!this.current(version)) return;
        const next = validatedSection(data, this.sectionId);
        this.parts.content.innerHTML = next.content.innerHTML;
        this.parts = cartParts(this);
        this.syncNoteVisibility(data.item_count);
        for (const count of document.querySelectorAll('[data-header-action="cart"] [data-cart-count-bubble]')) count.textContent = String(data.item_count);
        this.showStatus('updated');
      } catch (error) {
        if (!this.current(version)) return;
        this.resetQuantities();
        this.showError(error.cartMessage);
      } finally {
        if (this.current(version)) {
          this.setBusy(false);
          this.restoreFocus(focus);
        }
      }
    }

    resetQuantity(input) {
      input.value = input.getAttribute('value');
      input.setCustomValidity('');
      input.closest('ai-cart-quantity-input')?.refresh?.();
    }

    resetQuantities() {
      for (const input of this.querySelectorAll(QUANTITY)) this.resetQuantity(input);
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.formWasInert = this.parts.form.hasAttribute('inert');
        this.parts.form.setAttribute('inert', '');
        this.disabledControls = [...this.parts.form.querySelectorAll('input, button, select, textarea')].map(node => ({ node, disabled: node.disabled }));
        for (const { node } of this.disabledControls) node.disabled = true;
      } else {
        if (!this.formWasInert) this.parts.form.removeAttribute('inert');
        for (const { node, disabled } of this.disabledControls || []) if (this.contains(node)) node.disabled = disabled;
        this.disabledControls = [];
        for (const quantity of this.querySelectorAll('ai-cart-quantity-input')) quantity.refresh?.();
      }
    }

    showStatus(value) {
      for (const text of this.querySelectorAll('[data-cart-status-text]')) text.hidden = text.dataset.cartStatusText !== value;
    }

    clearErrors() {
      if (this.error) this.error.hidden = true;
      if (this.errorMessage) this.errorMessage.textContent = this.defaultError;
      for (const error of this.querySelectorAll('[data-cart-item-error]')) { error.textContent = ''; error.hidden = true; }
    }

    showError(message) {
      if (this.errorMessage) this.errorMessage.textContent = message || this.defaultError;
      if (this.error) this.error.hidden = false;
      this.showStatus(null);
    }

    restoreFocus(previous) {
      const rows = this.parts.rows;
      const same = rows.find(row => row.dataset.cartKey === previous.key);
      const row = same || rows[Math.min(previous.index, rows.length - 1)];
      let target;
      if (same && previous.action) target = same.querySelector(`[data-cart-quantity-action="${previous.action}"]:not(:disabled)`);
      if (same && previous.remove) target = same.querySelector('[data-cart-remove]');
      target ||= row?.querySelector(`${QUANTITY}:not(:disabled)`);
      target ||= this.parts.empty.querySelector('a[href], button:not(:disabled)') || this.querySelector('h1, h2');
      if (!target) return;
      if (!target.matches('input, button, a[href]')) target.tabIndex = -1;
      target.focus({ preventScroll: true });
    }

    syncNoteVisibility(count) {
      if (!this.note) return;
      this.note.hidden = count === 0;
      for (const label of this.note.labels || []) label.hidden = count === 0;
      if (count === 0) clearTimeout(this.noteTimer);
    }

    async saveNote() {
      if (!this.isConnected) return;
      const version = this.version;
      const note = this.note.value;
      try {
        const data = await this.post(this.dataset.cartUpdateUrl, { note });
        if (!this.current(version)) return;
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid note response.');
      } catch (error) {
        if (this.current(version)) {
          const fallback = document.documentElement.lang.startsWith('zh') ? '备注未能保存。' : 'Your note could not be saved.';
          this.showError(error.cartMessage || fallback);
        }
      }
    }
  }

  if (!customElements.get('ai-cart-items')) customElements.define('ai-cart-items', AiCartItems);
})();
